// First-party cookies that govern the landing site's ad-sharing opt-out.
//
// Contract (per compliance.md §1):
//   - `fulton_ad_optout` is "true" when the user (or GPC) has opted out of
//     data sharing with advertising partners. Any other value (including
//     "false" or absent) means sharing is allowed. The Pixel server-side
//     gate treats only "true" as opted-out.
//   - `fulton_gpc_applied` is "true" iff the middleware originally set the
//     opt-out from a `Sec-GPC: 1` header. The modal renders the GPC note
//     when this is set, regardless of whether the user has since toggled.
//
// Cross-surface sync (parent compliance.md §1 "Shared infrastructure"):
//   In production, `fulton_ad_optout` is scoped to `Domain=.askfulton.com`
//   so the authenticated app at `app.askfulton.com` receives it. The
//   companion `AdSharingBackfill` component in fulton-app reads it on
//   first authenticated mount and mirrors the opt-out to the user's
//   Convex row. Dev (`NODE_ENV !== 'production'`) leaves the Domain
//   attribute off, so localhost and Railway preview hosts get a normal
//   host-scoped cookie.

export const AD_OPTOUT_COOKIE = 'fulton_ad_optout'
export const ANALYTICS_OPTOUT_COOKIE = 'fulton_analytics_optout'
export const GPC_APPLIED_COOKIE = 'fulton_gpc_applied'
export const ONE_YEAR_SECONDS = 60 * 60 * 24 * 365

/**
 * Production-only cookie domain. `.askfulton.com` lets both `askfulton.com`
 * (this landing site) and `app.askfulton.com` (the authenticated app) read
 * the cookie — that's what powers cross-surface sync of the opt-out on
 * signup/login.
 *
 * Returns `undefined` in dev so the browser falls back to host-scoped
 * cookies (which is what works on localhost / Railway preview hosts).
 */
function getCrossSurfaceCookieDomain(): string | undefined {
  if (typeof process === 'undefined') return undefined
  if (process.env.NODE_ENV !== 'production') return undefined
  return '.askfulton.com'
}

/**
 * Read a cookie value from `document.cookie`. Safe to call on the server
 * (returns null when `document` is unavailable). For server reads from
 * Next.js Route Handlers / Server Components, use `cookies()` from
 * `next/headers` directly instead — this helper is the client path.
 */
function readClientCookie(name: string): string | null {
  if (typeof document === 'undefined') return null
  const target = `${name}=`
  for (const entry of document.cookie.split(';')) {
    const trimmed = entry.trim()
    if (trimmed.startsWith(target)) return trimmed.slice(target.length)
  }
  return null
}

/** Browser-side helper: returns true when the user is opted out of ad
 *  sharing (the Marketing category — gates the Facebook Pixel). */
export function readAdOptOut(): boolean {
  return readClientCookie(AD_OPTOUT_COOKIE) === 'true'
}

/** Browser-side helper: returns true when the user is opted out of
 *  analytics (the Analytics category — gates PostHog capture). */
export function readAnalyticsOptOut(): boolean {
  return readClientCookie(ANALYTICS_OPTOUT_COOKIE) === 'true'
}

/** Browser-side helper: returns true when the opt-out was set via GPC. */
export function readGpcApplied(): boolean {
  return readClientCookie(GPC_APPLIED_COOKIE) === 'true'
}

/**
 * Browser-side helper: persist the user's choice.
 * `true` ⇒ opted out (Pixel will not render on next request).
 * `false` ⇒ opted in (default — Pixel renders).
 *
 * We always write a literal string so server-side reads can compare
 * unambiguously. SameSite=Lax matches our other cookies; Secure is added
 * automatically by the browser on https origins. In production, the
 * `Domain=.askfulton.com` attribute carries the preference across to the
 * authenticated app (see `getCrossSurfaceCookieDomain`).
 */
export function writeAdOptOut(optedOut: boolean): void {
  writeOptOutCookie(AD_OPTOUT_COOKIE, optedOut)
}

/**
 * Browser-side helper: persist the user's analytics choice.
 * `true` ⇒ opted out (PostHog calls `opt_out_capturing`).
 * `false` ⇒ opted in (default).
 *
 * Cookie shape mirrors `fulton_ad_optout` exactly so the cross-surface
 * sync via `Domain=.askfulton.com` in prod works the same way (the app
 * subdomain receives both opt-out signals on login).
 */
export function writeAnalyticsOptOut(optedOut: boolean): void {
  writeOptOutCookie(ANALYTICS_OPTOUT_COOKIE, optedOut)
}

/**
 * Is the browser sending a Global Privacy Control signal right now?
 *
 * The JS-side twin of the `Sec-GPC: 1` request header — every GPC browser
 * exposes both. We need the JS side because `src/proxy.ts` only sees the
 * header on requests that actually reach the origin, and an edge cache HIT
 * never does.
 */
export function isGpcSignalled(): boolean {
  if (typeof navigator === 'undefined') return false
  return (
    (navigator as Navigator & { globalPrivacyControl?: boolean }).globalPrivacyControl === true
  )
}

/**
 * Browser-side consent gate for advertising scripts (the Meta Pixel).
 *
 * Two ways to be opted out:
 *   1. `fulton_ad_optout=true` — the dialog, or a proxy write on an earlier
 *      uncached request.
 *   2. A live GPC signal with **no cookie yet** — a first-time GPC visitor
 *      whose page came from the edge cache, so no proxy ran to write one.
 *
 * An explicit `fulton_ad_optout=false` wins over the GPC signal: that's the user
 * deliberately opting back in, and the same override rule the proxy applies.
 */
export function marketingAllowed(): boolean {
  if (readAdOptOut()) return false
  return !(isGpcSignalled() && isPrivacyCookieUnset(AD_OPTOUT_COOKIE))
}

/** Analytics (PostHog) twin of `marketingAllowed`. Same two rules. */
export function analyticsAllowed(): boolean {
  if (readAnalyticsOptOut()) return false
  return !(isGpcSignalled() && isPrivacyCookieUnset(ANALYTICS_OPTOUT_COOKIE))
}

/**
 * Browser-side helper: record that a GPC signal drove the opt-out, so the
 * dialog's status indicator can read "Opt-Out Signal Honored".
 *
 * Sticky by convention: written once when GPC is first seen, and never
 * cleared by a later manual toggle (the ", User Overridden" suffix covers
 * that case). Mirrors what `src/proxy.ts` writes server-side.
 */
export function writeGpcApplied(applied: boolean): void {
  writeOptOutCookie(GPC_APPLIED_COOKIE, applied)
}

/**
 * Browser-side helper: is this cookie **completely absent**?
 *
 * The GPC guard turns on this distinction. An explicit `"false"` means the
 * user opted back in and must be left alone; only a missing cookie means
 * "never seen this visitor". `readAdOptOut()` and friends collapse both to
 * `false`, which is the wrong test for that decision.
 */
export function isPrivacyCookieUnset(name: string): boolean {
  const value = readClientCookie(name)
  // An empty value counts as unset — it carries no decision. (A cookie
  // expired with `Max-Age=0` can linger as `name=` rather than disappearing,
  // depending on the environment.)
  return value === null || value === ''
}

/** Shared cookie writer for the opt-out family. SameSite=Lax + Path=/ +
 *  Max-Age=1y + optional Domain=.askfulton.com in prod. */
function writeOptOutCookie(name: string, optedOut: boolean): void {
  if (typeof document === 'undefined') return
  const value = optedOut ? 'true' : 'false'
  const domain = getCrossSurfaceCookieDomain()
  const parts = [
    `${name}=${value}`,
    `Max-Age=${ONE_YEAR_SECONDS}`,
    'Path=/',
    'SameSite=Lax',
  ]
  if (domain) parts.push(`Domain=${domain}`)
  document.cookie = parts.join('; ')
}

/**
 * Production cookie-attribute payload used by middleware writes. Mirrors
 * `writeAdOptOut`'s prod attributes (Domain, SameSite, Path, Max-Age).
 * Kept here so the middleware doesn't have to re-derive the domain rule.
 */
export function getServerCookieAttributes(): {
  maxAge: number
  sameSite: 'lax'
  secure: boolean
  path: '/'
  domain?: string
} {
  return {
    maxAge: ONE_YEAR_SECONDS,
    sameSite: 'lax',
    secure: process.env.NODE_ENV === 'production',
    path: '/',
    domain: getCrossSurfaceCookieDomain(),
  }
}
